import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Upload, CheckCircle, AlertCircle, FileUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function BulkUploadProgress({ endpoint, extraFields = {}, accept = ".csv,.xlsx", onComplete, onClose, title = "Bulk Upload", description }) {
  const [phase, setPhase] = useState('idle'); // idle | uploading | processing | done | error
  const [progress, setProgress] = useState({ processed: 0, total: 0, added: 0 });
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const fileRef = useRef(null);

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;

    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    Object.entries(extraFields).forEach(([key, val]) => formData.append(key, val));

    setPhase('uploading');
    setProgress({ processed: 0, total: 0, added: 0 });
    setResult(null);
    setErrorMsg('');

    try {
      const response = await fetch(`${BACKEND_URL}/api${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.detail || `Upload failed (${response.status})`);
      }

      setPhase('processing');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const event = JSON.parse(line);
            if (event.type === 'start') {
              setProgress(p => ({ ...p, total: event.total }));
            } else if (event.type === 'progress') {
              setProgress({ processed: event.processed, total: event.total, added: event.added });
            } else if (event.type === 'done') {
              setResult(event);
              setPhase('done');
            } else if (event.type === 'error') {
              setErrorMsg(event.message);
              setPhase('error');
            }
          } catch {}
        }
      }
    } catch (err) {
      setErrorMsg(err.message || 'Upload failed');
      setPhase('error');
    }
  };

  const pct = progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0;

  return (
    <div className="space-y-4" data-testid="bulk-upload-progress">
      {/* File Selection */}
      {phase === 'idle' && (
        <>
          {description && <p className="text-sm text-gray-600">{description}</p>}
          <div className="space-y-2">
            <Label htmlFor="bulk-file">Select File</Label>
            <Input
              id="bulk-file"
              ref={fileRef}
              type="file"
              accept={accept}
              data-testid="bulk-upload-file-input"
            />
          </div>
          <div className="flex justify-end gap-2">
            {onClose && (
              <Button variant="outline" onClick={onClose} data-testid="bulk-upload-cancel-btn">Cancel</Button>
            )}
            <Button onClick={handleUpload} className="bg-blue-600 hover:bg-blue-700" data-testid="bulk-upload-start-btn">
              <Upload className="w-4 h-4 mr-2" />
              Upload & Process
            </Button>
          </div>
        </>
      )}

      {/* Uploading / Processing */}
      {(phase === 'uploading' || phase === 'processing') && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <FileUp className="w-5 h-5 text-blue-600 animate-pulse" />
            <span className="font-medium text-gray-900">
              {phase === 'uploading' ? 'Uploading file...' : `Processing records...`}
            </span>
          </div>

          {/* Progress bar */}
          <div className="space-y-2">
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300 ease-out bg-blue-600"
                style={{ width: `${phase === 'uploading' ? 5 : pct}%` }}
                data-testid="bulk-upload-progress-bar"
              />
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span data-testid="bulk-upload-progress-text">
                {phase === 'processing' && progress.total > 0
                  ? `${progress.processed} of ${progress.total} records processed`
                  : 'Preparing...'}
              </span>
              <span className="font-medium" data-testid="bulk-upload-progress-pct">
                {phase === 'processing' ? `${pct}%` : ''}
              </span>
            </div>
            {progress.added > 0 && (
              <p className="text-sm text-green-700">{progress.added} added successfully so far</p>
            )}
          </div>
        </div>
      )}

      {/* Done */}
      {phase === 'done' && result && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-green-900" data-testid="bulk-upload-success-msg">
                Upload Complete: {result.added} of {result.total} records added
              </p>
              {result.errors && result.errors.length > 0 && (
                <p className="text-sm text-amber-700 mt-1">{result.errors.length} row(s) skipped</p>
              )}
            </div>
          </div>

          {/* Progress bar at 100% */}
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div className="h-full rounded-full bg-green-500 w-full" />
          </div>

          {/* Error details */}
          {result.errors && result.errors.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer text-amber-700 font-medium">View skipped rows</summary>
              <div className="mt-2 max-h-40 overflow-y-auto bg-amber-50 border border-amber-200 rounded p-3 space-y-1">
                {result.errors.map((err, i) => (
                  <p key={i} className="text-amber-800">{err}</p>
                ))}
              </div>
            </details>
          )}

          <div className="flex justify-end">
            <Button
              onClick={() => { onComplete?.(); onClose?.(); }}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="bulk-upload-done-btn"
            >
              Done
            </Button>
          </div>
        </div>
      )}

      {/* Error */}
      {phase === 'error' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
            <p className="font-medium text-red-900" data-testid="bulk-upload-error-msg">{errorMsg}</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setPhase('idle'); setErrorMsg(''); }} data-testid="bulk-upload-retry-btn">
              Try Again
            </Button>
            {onClose && <Button variant="outline" onClick={onClose}>Close</Button>}
          </div>
        </div>
      )}
    </div>
  );
}

export default BulkUploadProgress;
